// PDF print CSS injected into the print iframe's document. Two things worth
// calling out:
//   - @font-face loads Roboto from a same-origin URL (Vite asset), keeping the
//     fetch within patron's CSP.
//   - font-weight spans 100–900 over the single 400-weight file so the browser
//     synthesizes the heavier "Scan me!" label without fetching a 900-weight
//     font.
export const buildPrintStyles = (robotoFontUrl: string): string => `
            @font-face {
              font-family: 'Roboto';
              font-weight: 100 900;
              font-style: normal;
              src: url('${robotoFontUrl}') format('woff2');
            }
            @page { margin: 20px 24px; size: A4; }
            /* Browsers skip background colors in print output unless the user
               ticks "Background graphics". print-color-adjust: exact forces
               them on every element so the QR card, topic pills, avatar
               fallback, and article backgrounds always render in the PDF. */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* !important to override any margins/padding from the article's own CSS
               (pony's legacy template loads an external Froala stylesheet that may
               add body/html spacing we cannot predict). */
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              height: auto !important;
              min-height: 0 !important;
              display: block !important;
            }
            body { font-family: 'Inter', system-ui, -apple-system, Arial, sans-serif; }

            /* ── Top metadata card ─────────────────────────────── */
            .pdf-top {
              display: flex;
              justify-content: space-between;
              align-items: stretch;
              padding: 16px 0 20px;
              page-break-inside: avoid;
            }
            .pdf-top-meta {
              flex: 1;
              min-width: 0;
              min-height: 33mm;
              padding-right: 24px;
              display: flex;
              flex-direction: column;
            }
            .pdf-top-title {
              font-size: 16px;
              font-weight: 600;
              color: #1b1a1f;
              margin: 0 0 6px;
              line-height: 1.5;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 3;
              overflow: hidden;
              max-height: calc(1.5em * 3);
            }
            .pdf-top-byline {
              font-size: 14px;
              font-weight: 400;
              color: #676570;
              margin-top: auto;
              padding-top: 28px;
              line-height: 1.5;
            }
            /* QR block — folder-tab shape via clip-path. The path is authored
               for a 111×127px card (matching .pdf-top-qr-card below); the
               S-curve at the top-right (H 71 … C 79 13 83 16 88 16) creates the
               concave notch. Adjust both the path and the card width/height
               together if the tab size changes. */
            .pdf-top-qr {
              flex-shrink: 0;
            }
            .pdf-top-qr-card {
              position: relative;
              width: 111px;
              height: 127px;
              background: #000000;
              clip-path: path("M 9 0 H 71 C 78 0 79 3 79 8 C 79 13 83 16 88 16 H 102 C 109 16 111 18 111 24 V 118 C 111 123 107 127 102 127 H 9 C 4 127 0 123 0 118 V 9 C 0 4 4 0 9 0 Z");
            }
            .pdf-top-qr-label {
              position: absolute;
              top: 7px;
              left: 10px;
              color: #fff;
              font-family: 'Roboto', sans-serif;
              font-size: 12px;
              font-weight: 900;
              letter-spacing: 0.5px;
              line-height: 1;
              white-space: nowrap;
            }
            .pdf-top-qr-frame {
              position: absolute;
              left: 10px;
              top: 26px;
              background: white;
              border-radius: 3px;
              padding: 2px;
              line-height: 0;
            }
            .pdf-top-qr-frame svg {
              display: block;
              width: 87px !important;
              height: 87px !important;
            }

            /* ── Info-header row ───────────────────────────────── */
            .pdf-info-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 15px;
              position: relative;
              margin-bottom: 20px;
              page-break-inside: avoid;
              gap: 8px;
            }
            .pdf-info-header::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 15px;
              right: 15px;
              border-bottom: 1px solid #e4e3e8;
            }
            .pdf-info-source {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 12px;
              color: #676570;
              flex-shrink: 0;
            }
            .pdf-info-avatar {
              width: 28px;
              height: 28px;
              border-radius: 50%;
              object-fit: cover;
              flex-shrink: 0;
            }
            .pdf-info-avatar-fallback {
              width: 28px;
              height: 28px;
              border-radius: 50%;
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 11px;
              font-weight: 700;
              color: #fff;
            }
            .pdf-info-source-name {
              font-size: 14px;
              font-weight: 600;
              color: #1b1a1f;
            }
            .pdf-info-dot { color: #c8c7cd; }
            .pdf-info-channels {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              justify-content: flex-end;
            }
            .pdf-info-tag {
              font-size: 12px;
              color: #1b1a1f;
              background: #f1f0f7;
              border-radius: 100vh;
              padding: 4px 16px;
              line-height: 1.5;
              white-space: nowrap;
            }

            /* ── Page-break rules ──────────────────────────────── */
            /* Reset any forced page breaks baked into the article's own styles.
               CMS/email HTML often injects page-break-before:always on section
               dividers. We use !important so this overrides embedded <style> rules
               (inline style !important still wins — those are handled in the JS
               cleanup pass below). */
            * {
              page-break-before: auto !important;
              page-break-after: auto !important;
              break-before: auto !important;
              break-after: auto !important;
            }
            /* Atomic elements that must never split: images and figures only.
               Tables are intentionally excluded — in email/CMS HTML, tables are
               full-page layout containers, not data tables. Applying break-inside:avoid
               to a table taller than the page causes Chromium to leave a blank gap
               on the previous page then render the table across pages anyway. */
            img, figure {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            /* SVG icons are generally small enough to treat atomically */
            svg {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            /* pre/code blocks — keep together if they fit */
            pre, code {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            /* Headings must not split (they're one line anyway) */
            h1, h2, h3, h4, h5, h6 {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            /* Keep individual list items intact, but let the whole list split */
            li {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            /* Repeat table headers on every page */
            thead {
              display: table-header-group;
            }
            /* Heading + next-sibling wrapper injected by JS below */
            .pdf-keep-with-next {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            /* p, ul, ol, blockquote, table, tr — intentionally NOT avoided,
               so they can split naturally and reduce blank gaps */

            /* Collapse empty spacer cells common in email/CMS HTML */
            tr:empty, td:empty, th:empty {
              height: auto !important;
              min-height: 0 !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
              font-size: 0 !important;
              line-height: 0 !important;
            }

            /* Poll fallback: print-frame waits for polls to hydrate, but if one
               can't load in time, hide its loading affordance so the PDF never
               prints a spinner / "Loading …". A hydrated poll's options carry
               the -js-ready class and render normally. */
            .spinner-dualRing { display: none !important; }
            .-js-options:not(.-js-ready) { display: none !important; }

            /* Preserve MJML column widths in print mode.
               MJML embeds ".mj-outlook-group-fix { width:100% !important }" with no
               media query. In screen mode the "@media only screen" column rules
               override it, but in print mode those media rules don't apply, so every
               column collapses to 100% width, stacking multi-column rows and inflating
               the document height. Our injected style is appended after the article's
               own <style> tags so same-specificity + !important declarations here win
               via last-declaration order. */
            .mj-column-per-22 { width: 22% !important; max-width: 22% !important; }
            .mj-column-per-78 { width: 78% !important; max-width: 78% !important; }
            .mj-column-per-50 { width: 50% !important; max-width: 50% !important; }

            /* ── Print-mode MJML column layout fix ────────────────────────────────
               MJML's inline-block column divs sit inside a td with font-size:0 /
               line-height:0 (the "zero-strut" trick). In Chromium's print renderer
               this inline formatting context can mis-position columns. Switching the
               mj-column-per-100 wrapper to display:flex puts the inner columns into
               a flex context, giving Chromium a stable layout model and preserving
               the intended side-by-side arrangement. */
            @media print {
              .mj-column-per-100 {
                display: flex !important;
                flex-wrap: wrap !important;
                width: 100% !important;
              }
              /* Use inline-block (not block) so columns without a flex parent
                 (e.g. 50/50 image grids whose columns sit directly inside a td)
                 still flow side-by-side. When inside a flex parent
                 (.mj-column-per-100), flex context overrides display anyway.
                 Do NOT override vertical-align — MJML sets top/middle inline
                 on each column and those values must be preserved. */
              .mj-column-per-22 {
                display: inline-block !important;
                flex: 0 0 22% !important;
              }
              .mj-column-per-50 {
                display: inline-block !important;
                flex: 0 0 50% !important;
              }
              .mj-column-per-78 {
                display: inline-block !important;
                flex: 0 0 78% !important;
              }
            }
          `;
